<script lang="ts" setup>
/* eslint-disable no-console */
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { brand, brandConfig, features } from '@/config/brands'
import { pushAxonEvent } from '@/utils/axonTrack'

const ADSENSE_CLIENT_ID = brandConfig.texts?.adsenseClientId || ''
const ADSENSE_SLOT_ID = brandConfig.texts?.adsenseSlotId || ''
const AD_HOST_KEYWORD = brandConfig.texts?.adSenseHostKeyword || ''
const tag = `[Ad-${brand}]`

let warnedHost = false

const enableAdHost = computed(() => {
  if (!AD_HOST_KEYWORD)
    return true
  const keywords = AD_HOST_KEYWORD.split(',').map(k => k.trim())
  return keywords.some(k => location.hostname.includes(k))
})

const isReady = computed(() => {
  if (!features?.enableAdSense) {
    if (!warnedHost) {
      warnedHost = true
      console.warn(tag, '广告功能未开启')
    }
    return false
  }
  if (!enableAdHost.value) {
    if (!warnedHost) {
      warnedHost = true
      console.warn(tag, `域名不包含 ${AD_HOST_KEYWORD}`)
    }
    return false
  }
  return true
})

const sentinelRef = ref<HTMLElement>()
let wrapperEl: HTMLElement | null = null

let statusObserver: MutationObserver | null = null
let statusTimer: ReturnType<typeof setTimeout> | null = null

function cleanup() {
  statusObserver?.disconnect()
  statusObserver = null
  if (statusTimer) {
    clearTimeout(statusTimer)
    statusTimer = null
  }
}

function reportVisible() {
  pushAxonEvent('purchase', {
    currency: 'USD',
    value: 0.001,
    transaction_id: `ad_${ADSENSE_SLOT_ID}_${Date.now()}`,
    items: [{
      item_id: `ad_slot_${ADSENSE_SLOT_ID}`,
      item_name: `AdSense_Slot_${ADSENSE_SLOT_ID}`,
      price: 0,
      quantity: 1,
    }],
  }, { is_taboola_ad_purchase: true })
}

function observeVisibility(el: HTMLElement) {
  if (el.getBoundingClientRect().top < window.innerHeight) {
    reportVisible()
    return
  }
  const visObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting)
        continue
      reportVisible()
      visObserver.disconnect()
    }
  }, { threshold: 0.5 })
  visObserver.observe(el)
}

function loadAdsense(el: HTMLElement) {
  const ins = el.querySelector('ins.adsbygoogle') as HTMLElement | null
  if (!ins)
    return

  try {
    ;(window as any).adsbygoogle = (window as any).adsbygoogle || []
    ;(window as any).adsbygoogle.push({})
    console.log(tag, 'AdSense push 已执行', `slot=${ADSENSE_SLOT_ID}`)
  }
  catch (err) {
    console.error(tag, 'AdSense push 失败', err, `slot=${ADSENSE_SLOT_ID}`)
    switchToFallback()
    return
  }

  statusObserver = new MutationObserver(() => {
    const status = ins.getAttribute('data-ad-status')
    if (status === 'filled') {
      el.style.cssText = 'display:flex;justify-content:center;width:100%;padding:16px 0;visibility:visible'
      console.log(tag, 'AdSense 填充成功', `slot=${ADSENSE_SLOT_ID}`)
      cleanup()
      observeVisibility(el)
    }
    else if (status === 'unfilled' || status === 'unfill-optimized') {
      console.warn(tag, `AdSense ${status}，无备选可切换`, `slot=${ADSENSE_SLOT_ID}`)
      statusObserver?.disconnect()
      switchToFallback()
    }
  })
  statusObserver.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })

  statusTimer = setTimeout(() => {
    if (ins.getAttribute('data-ad-status') !== 'filled') {
      console.warn(tag, 'AdSense 超时 (5s) 未填充，无备选可切换', `slot=${ADSENSE_SLOT_ID}`)
      statusObserver?.disconnect()
      switchToFallback()
    }
  }, 5000)
}

function removeWrapper() {
  if (wrapperEl && wrapperEl.parentNode)
    wrapperEl.parentNode.removeChild(wrapperEl)
  wrapperEl = null
}

function switchToFallback() {
  cleanup()
  removeWrapper()
  console.warn(tag, '广告填充失败，无可用备选')
}

function createAdWrapper(): HTMLElement | null {
  const sentinel = sentinelRef.value
  if (!sentinel || !sentinel.parentNode)
    return null

  const wrapper = document.createElement('div')
  wrapper.className = 'chapter-ad-wrapper'
  wrapper.style.cssText = 'width:100%;max-height:0;overflow:hidden;visibility:hidden'

  const ins = document.createElement('ins')
  ins.className = 'adsbygoogle'
  ins.style.cssText = 'display:block;width:100%'
  ins.setAttribute('data-ad-layout', 'in-article')
  ins.setAttribute('data-ad-format', 'fluid')
  ins.setAttribute('data-ad-client', ADSENSE_CLIENT_ID)
  ins.setAttribute('data-ad-slot', ADSENSE_SLOT_ID)

  wrapper.appendChild(ins)
  sentinel.parentNode.insertBefore(wrapper, sentinel.nextSibling)
  wrapperEl = wrapper
  return wrapper
}

onMounted(() => {
  if (!isReady.value)
    return

  if (!ADSENSE_CLIENT_ID || !ADSENSE_SLOT_ID) {
    console.warn(tag, '所有广告源配置缺失')
    return
  }

  nextTick(() => {
    const el = sentinelRef.value
    if (!el)
      return

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting)
          continue
        observer.disconnect()
        console.log(tag, '本次广告: adsense', `slot=${ADSENSE_SLOT_ID}`)
        const wrapper = createAdWrapper()
        if (wrapper)
          loadAdsense(wrapper)
      }
    }, { rootMargin: '0px' })
    observer.observe(el)
  })
})

onUnmounted(() => {
  cleanup()
})
</script>

<template>
  <div ref="sentinelRef" class="chapter-ad-sentinel" />
</template>

<style scoped>
.chapter-ad-sentinel {
  height: 1px;
  pointer-events: none;
}
</style>
